import type { ReplayTeamColors } from "@Components/game/session-results/feedback/visualization/constants";
import { TEAM_COLORS, VISUALIZATION_LAYOUT, replayToPitchCoords } from "@Components/game/session-results/feedback/visualization/constants";
import {
  DEFAULT_HEATMAP_RENDER_CONFIG,
  HEATMAP_OPACITY,
  HEATMAP_STABILITY_EPSILON,
  VR_HEATMAP_RENDER_CONFIG,
  type HeatmapRenderConfig,
} from "@Components/game/session-results/feedback/visualization/heatmap-config";
import { AppContext } from "@Context/common/general/context";
import { calculateHeatmapGrid, type HeatmapGrid, type HeatmapSnapshotPlayer } from "@beyourbest/decision-engine";
import { BaseTexture, SCALE_MODES, Texture } from "@pixi/core";
import { Graphics as PixiGraphics } from "@pixi/graphics";
import { Container, Graphics, Sprite } from "@pixi/react";
import { Sprite as PixiSprite } from "@pixi/sprite";
import { useContext, useEffect, useMemo, useRef } from "react";

interface CharacterState {
  id: string;
  x: number;
  y: number;
  team: number | string;
  isPlayer?: boolean;
}

interface HeatmapRendererProps {
  characterStates: CharacterState[];
  showHeatmap: boolean;
  currentTime: number;
  teamColors?: ReplayTeamColors;
}

const PITCH = VISUALIZATION_LAYOUT.pitch;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const createHeatmapTexture = () => {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(PITCH.width));
  canvas.height = Math.max(1, Math.round(PITCH.height));

  const offscreen = document.createElement("canvas");
  offscreen.width = 1;
  offscreen.height = 1;

  const context = canvas.getContext("2d");
  const offscreenContext = offscreen.getContext("2d");
  const texture = new Texture(new BaseTexture(canvas, { scaleMode: SCALE_MODES.LINEAR }));

  return { canvas, context, offscreen, offscreenContext, texture };
};

const getCellStyle = (value: number, teamColors: ReplayTeamColors) => {
  const control = clamp(Number.isFinite(value) ? value : 0, 0, 1);
  const diff = control - 0.5;
  const intensity = Math.min(1, Math.abs(diff) * 2.5);
  const alpha = 0.15 + 0.55 * intensity;

  return {
    alpha,
    color: diff >= 0 ? teamColors.home.rgb : teamColors.away.rgb,
  };
};

const clearHeatmap = (context: CanvasRenderingContext2D, texture: Texture, canvas: HTMLCanvasElement) => {
  context.clearRect(0, 0, canvas.width, canvas.height);
  texture.update();
};

const getInterpolatedValue = (grid: HeatmapGrid, sampleX: number, sampleY: number) => {
  const x = clamp(sampleX, 0, grid.cols - 1);
  const y = clamp(sampleY, 0, grid.rows - 1);
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(grid.cols - 1, x0 + 1);
  const y1 = Math.min(grid.rows - 1, y0 + 1);
  const tx = x - x0;
  const ty = y - y0;
  const top = grid.grid[y0][x0] * (1 - tx) + grid.grid[y0][x1] * tx;
  const bottom = grid.grid[y1][x0] * (1 - tx) + grid.grid[y1][x1] * tx;

  return top * (1 - ty) + bottom * ty;
};

const smoothGridValues = (values: number[][], passes: number) => {
  let current = values.map(row => row.slice());

  for (let pass = 0; pass < passes; pass++) {
    const next = current.map(row => row.slice());

    for (let row = 0; row < current.length; row++) {
      for (let col = 0; col < current[row].length; col++) {
        let total = current[row][col] * 4;
        let weight = 4;

        for (let rowOffset = -1; rowOffset <= 1; rowOffset++) {
          for (let colOffset = -1; colOffset <= 1; colOffset++) {
            if (rowOffset === 0 && colOffset === 0) continue;

            const neighborRow = row + rowOffset;
            const neighborCol = col + colOffset;
            if (neighborRow < 0 || neighborRow >= current.length || neighborCol < 0 || neighborCol >= current[row].length) {
              continue;
            }

            const neighborWeight = rowOffset === 0 || colOffset === 0 ? 2 : 1;
            total += current[neighborRow][neighborCol] * neighborWeight;
            weight += neighborWeight;
          }
        }

        next[row][col] = total / weight;
      }
    }

    current = next;
  }

  return current;
};

const blendGridValues = (previous: number[][] | null, next: number[][], amount: number) => {
  if (!previous || previous.length !== next.length || previous[0]?.length !== next[0]?.length) {
    return next.map(row => row.slice());
  }

  return next.map((row, rowIndex) =>
    row.map((value, colIndex) => {
      const previousValue = previous[rowIndex][colIndex];
      const delta = value - previousValue;

      if (Math.abs(delta) < HEATMAP_STABILITY_EPSILON) {
        return previousValue;
      }

      return previousValue * (1 - amount) + value * amount;
    })
  );
};

interface PaintHeatmapArgs {
  context: CanvasRenderingContext2D;
  canvas: HTMLCanvasElement;
  offscreen: HTMLCanvasElement;
  offscreenContext: CanvasRenderingContext2D | null;
  texture: Texture;
  heatmap: HeatmapGrid;
  previousGrid: number[][] | null;
  teamColors: ReplayTeamColors;
  renderConfig: HeatmapRenderConfig;
}

const paintHeatmap = ({
  context,
  canvas,
  offscreen,
  offscreenContext,
  texture,
  heatmap,
  previousGrid,
  teamColors,
  renderConfig,
}: PaintHeatmapArgs) => {
  if (heatmap.cols <= 0 || heatmap.rows <= 0) {
    clearHeatmap(context, texture, canvas);
    return previousGrid;
  }

  const displayWidth = Math.max(1, Math.round(PITCH.width));
  const displayHeight = Math.max(1, Math.round(PITCH.height));

  if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
    canvas.width = displayWidth;
    canvas.height = displayHeight;
  }

  const offscreenWidth = Math.max(1, heatmap.cols * renderConfig.interpolationScale);
  const offscreenHeight = Math.max(1, heatmap.rows * renderConfig.interpolationScale);

  if (!offscreenContext) {
    clearHeatmap(context, texture, canvas);
    return previousGrid;
  }

  if (offscreen.width !== offscreenWidth || offscreen.height !== offscreenHeight) {
    offscreen.width = offscreenWidth;
    offscreen.height = offscreenHeight;
  }

  const smoothedGrid = smoothGridValues(heatmap.grid, renderConfig.spatialSmoothingPasses);
  const renderedGrid = blendGridValues(previousGrid, smoothedGrid, renderConfig.temporalSmoothing);
  const renderableGrid: HeatmapGrid = { ...heatmap, grid: renderedGrid };
  const imageData = offscreenContext.createImageData(offscreen.width, offscreen.height);
  const data = imageData.data;

  for (let row = 0; row < offscreen.height; row++) {
    for (let col = 0; col < offscreen.width; col++) {
      const sampleX = (col + 0.5) / renderConfig.interpolationScale - 0.5;
      const sampleY = (row + 0.5) / renderConfig.interpolationScale - 0.5;
      const { color, alpha } = getCellStyle(getInterpolatedValue(renderableGrid, sampleX, sampleY), teamColors);

      const pixelIndex = (row * offscreen.width + col) * 4;
      data[pixelIndex] = color.r;
      data[pixelIndex + 1] = color.g;
      data[pixelIndex + 2] = color.b;
      data[pixelIndex + 3] = Math.round(alpha * 255);
    }
  }

  offscreenContext.putImageData(imageData, 0, 0);
  context.clearRect(0, 0, displayWidth, displayHeight);
  context.save();
  context.translate(displayWidth, displayHeight);
  context.scale(-1, -1);
  context.filter = `blur(${renderConfig.softBlurPx}px)`;
  context.globalAlpha = 0.22;
  context.drawImage(offscreen, -10, -10, displayWidth + 20, displayHeight + 20);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = renderConfig.imageSmoothingQuality;
  context.globalAlpha = 0.92;
  context.drawImage(offscreen, -10, -10, displayWidth + 20, displayHeight + 20);
  context.restore();
  context.globalAlpha = 1;
  texture.update();

  return renderedGrid;
};

const drawMask = (graphics: PixiGraphics) => {
  graphics.clear();
  graphics.beginFill(0xffffff);
  graphics.drawRoundedRect(0, 0, PITCH.width, PITCH.height, PITCH.cornerRadius);
  graphics.endFill();
};

const HeatmapRenderer = ({ characterStates, showHeatmap, currentTime, teamColors = TEAM_COLORS }: HeatmapRendererProps) => {
  const { state } = useContext(AppContext);
  const renderConfig: HeatmapRenderConfig = state.device.isVR ? VR_HEATMAP_RENDER_CONFIG : DEFAULT_HEATMAP_RENDER_CONFIG;
  const maskRef = useRef<PixiGraphics | null>(null);
  const spriteRef = useRef<PixiSprite | null>(null);
  const previousRenderedGridRef = useRef<number[][] | null>(null);
  const previousTimeRef = useRef<number | null>(null);
  const previousTeamColorsKeyRef = useRef<string>("");
  const previousCharacterIdsKeyRef = useRef<string>("");
  const { canvas, context, offscreen, offscreenContext, texture } = useMemo(createHeatmapTexture, []);

  useEffect(() => () => texture.destroy(true), [texture]);

  useEffect(() => {
    if (spriteRef.current && maskRef.current) {
      spriteRef.current.mask = maskRef.current;
    }
  }, []);

  useEffect(() => {
    if (!showHeatmap || !context) return;

    if (characterStates.length === 0) {
      clearHeatmap(context, texture, canvas);
      previousRenderedGridRef.current = null;
      previousTimeRef.current = null;
      previousCharacterIdsKeyRef.current = "";
      return;
    }

    const characterIdsKey = characterStates
      .map(character => character.id)
      .sort()
      .join("\0");

    const hasCharacterSetChanged = previousCharacterIdsKeyRef.current !== characterIdsKey;

    if (hasCharacterSetChanged) {
      previousCharacterIdsKeyRef.current = characterIdsKey;
      previousTimeRef.current = null;
      previousRenderedGridRef.current = null;
    }

    const previousTime = previousTimeRef.current;
    const nextTeamColorsKey = `${teamColors.home.primary}:${teamColors.home.secondary}:${teamColors.home.tertiary}:${teamColors.away.primary}:${teamColors.away.secondary}:${teamColors.away.tertiary}`;
    const teamColorsChanged = previousTeamColorsKeyRef.current !== nextTeamColorsKey;
    let timeJumped = false;

    if (previousTime !== null) {
      const timeDelta = Math.abs(currentTime - previousTime);
      timeJumped = timeDelta > Math.max(0.5, renderConfig.updateIntervalSeconds * 2) || currentTime < previousTime;

      if (!timeJumped && !teamColorsChanged && timeDelta < renderConfig.updateIntervalSeconds) {
        return;
      }
    }

    const homeTeam = characterStates.find(player => player.isPlayer)?.team;
    const heatmapPlayers: HeatmapSnapshotPlayer[] = characterStates.map(({ id, x, y, team, isPlayer }) => {
      const pitchPosition = replayToPitchCoords(x, y);

      return {
        x: pitchPosition.x,
        y: pitchPosition.y,
        id,
        team: team === homeTeam ? "home" : "away",
        isPlayer,
        time: currentTime,
      };
    });

    const heatmap = calculateHeatmapGrid(heatmapPlayers);
    if (timeJumped) {
      previousRenderedGridRef.current = null;
    }

    previousRenderedGridRef.current = paintHeatmap({
      context,
      canvas,
      offscreen,
      offscreenContext,
      texture,
      heatmap,
      previousGrid: previousRenderedGridRef.current,
      teamColors,
      renderConfig,
    });
    previousTimeRef.current = currentTime;
    previousTeamColorsKeyRef.current = nextTeamColorsKey;
  }, [showHeatmap, context, texture, canvas, offscreen, offscreenContext, characterStates, currentTime, teamColors, renderConfig]);

  return (
    <Container x={PITCH.x} y={PITCH.y} alpha={HEATMAP_OPACITY} visible={showHeatmap}>
      <Graphics ref={maskRef} draw={drawMask} />
      <Sprite ref={spriteRef} texture={texture} width={PITCH.width} height={PITCH.height} />
    </Container>
  );
};

export default HeatmapRenderer;
